import { useCallback, useMemo, useState } from 'react';
import { GridColDef, GridRowId } from '@mui/x-data-grid-pro';
import { GridValidRowModel } from '@mui/x-data-grid/models/gridRows';

import { lenderUtilityCardsValidationSchema } from '@modules/admin/Tools/LenderUtility/validation';
import { gridDateTimeFormatter, newGridRecordId } from '@shared/commonFunctions';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { dataGridCol } from '@shared/components/grid/common';

import { GridColDateTime } from '@shared/components/grid/GridColDateTime';
import { useTable } from '@shared/hooks/useTable';
import { AlertModal } from '@shared/components/modals/AlertModal';
import { ILenderUtility, TCard } from '../../../../interfaces';
import { requestCopyCard, requestCreateCard, requestDeleteCard, requestUpdateCard } from './queries';

const columns: GridColDef[] = [
  dataGridCol('fromDate', {
    type: 'dateTime',
    flex: 1,
    editable: true,
    renderEditCell: (params) => <GridColDateTime params={params} />,
    valueFormatter: gridDateTimeFormatter,
  }),
  dataGridCol('toDate', {
    type: 'dateTime',
    flex: 1,
    editable: true,
    renderEditCell: (params) => <GridColDateTime params={params} />,
    valueFormatter: gridDateTimeFormatter,
  }),
  dataGridCol('updatedBy', {
    headerName: 'Updated By',
    valueGetter: ({ row }) => row.updatedBy?.name ?? '',
    flex: 1,
  }),
  dataGridCol('createdDate', {
    type: 'dateTime',
    headerName: 'Updated Date',
    flex: 1,
    valueFormatter: gridDateTimeFormatter,
  }),
];

interface ICardsProps {
  data?: TCard[];
  lenderId: string;
  selectedRowId?: GridRowId;
  lenderData?: ILenderUtility;
  onSelectRow(row: TCard): void;
}

export const Cards = ({ data = [], lenderId, lenderData, selectedRowId, onSelectRow }: ICardsProps) => {
  const { sortModel, setSortModel } = useTable({ sortModel: [{ field: 'fromDate', sort: 'desc' }] });
  const [showAlert, setShowAlert] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleAlertConfirm = useCallback(
    async () => {
      setLoading(true);
      await requestCopyCard(lenderId, showAlert ?? '');
      setLoading(false);
      setShowAlert(null);
    },
    [lenderId, showAlert]
  );

  const gridActions = useMemo(
    () => ({
      onSave: (row: GridValidRowModel) =>
        row?.id === newGridRecordId ? requestCreateCard(lenderId, row, lenderData) : requestUpdateCard(lenderId, row, lenderData),
      onCopy: (row: GridValidRowModel) => setShowAlert(row?.id),
      onDelete: (row: GridValidRowModel) => requestDeleteCard(lenderId, row?.id, lenderData),
    }),
    [lenderId]
  );

  const newRowModel = {
    fromDate: new Date(),
    createdDate: new Date(),
  };

  return (
    <>
      {!!showAlert && (
        <AlertModal
          title={'Alert'}
          subtitle="Copy Selected Card + Tiers & Conditions?"
          open={!!showAlert}
          buttonText={'Ok'}
          isLoading={loading}
          onConfirm={handleAlertConfirm}
          onClose={() => setShowAlert(null)}
        />
      )}

      <PhoenixGrid
        autoHeight
        withHeader
        sortModel={sortModel}
        name="card"
        columns={columns}
        loading={!data}
        rows={data}
        gridId={`card-${lenderId}`}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        actions={gridActions}
        newRowModel={newRowModel}
        validateSchema={lenderUtilityCardsValidationSchema}
        selectionModel={selectedRowId && [selectedRowId]}
        pageSize={5}
        onSortModelChange={setSortModel}
        onRowClick={({ row }) => onSelectRow(row as TCard)}
      />
    </>
  );
};
